import React from 'react';
import { 
  Award, 
  Users, 
  Factory, 
  Target, 
  TrendingUp, 
  Building2,
  Clock,
  Heart,
  CheckCircle,
  Quote,
  FileText,
  Maximize2,
  Gift,
  Sparkles
} from 'lucide-react';

const AboutPage = () => {
  // Why Choose Us items with icons
  const whyChooseUs = [
    {
      icon: Clock,
      title: 'Established Since 1991',
      description: 'Decades of proven experience'
    },
    {
      icon: Users,
      title: 'Family-Owned & Operated',
      description: 'Personal service and accountability'
    },
    {
      icon: Factory,
      title: 'In-House Production',
      description: 'Better quality control and faster turnaround'
    },
    {
      icon: Target,
      title: 'Detail-Driven Craftsmanship',
      description: 'Precision in every product'
    },
    {
      icon: TrendingUp,
      title: 'Scalable Solutions',
      description: 'From one item to enterprise programs'
    },
    {
      icon: Building2,
      title: 'Trusted by Corporate & Local Clients',
      description: 'Fortune-100 to small businesses'
    }
  ];

  // Values list
  const values = [
    'Craftsmanship',
    'Integrity',
    'Consistency',
    'Relationships'
  ];

  // Services list
  const services = [
    {
      icon: FileText,
      title: 'Small Format Printing',
      items: ['Business Cards', 'Flyers & Brochures', 'Catalogs & Booklets', 'Marketing Materials', 'Postcards & Mailers', 'Letterheads & Envelopes']
    },
    {
      icon: Maximize2,
      title: 'Large Format Graphics',
      items: ['Banners & Signs', 'Vehicle Wraps', 'Trade Show Displays', 'Window Graphics', 'Wall Murals', 'Outdoor Signage']
    },
    {
      icon: Award,
      title: 'Awards & Recognition',
      items: ['Crystal Awards', 'Trophies & Cups', 'Plaques & Plates', 'Medals & Ribbons', 'Corporate Awards', 'Custom Engraving']
    },
    {
      icon: Gift,
      title: 'Promotional Products',
      items: ['Branded Apparel', 'Corporate Gifts', 'Trade Show Giveaways', 'Custom Merchandise', 'Promotional Items', 'Employee Recognition']
    }
  ];

  return (
    <div className="min-h-screen bg-ivory-50" data-testid="about-page">
      {/* Hero Section */}
      <section className="relative gradient-hero py-16 md:py-20">
        <div className="absolute top-20 right-20 w-72 h-72 bg-gold-500/5 rounded-full blur-3xl" />
        <div className="absolute bottom-20 left-20 w-96 h-96 bg-gold-400/5 rounded-full blur-3xl" />
        
        <div className="relative container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-2xl sm:text-3xl md:text-4xl font-heading font-semibold text-ivory-800 leading-relaxed">
              Your trusted partner for custom awards, engraving, printing, and branded merchandise since 1991.
            </h1>
            <p className="text-sm md:text-base text-ivory-500 mt-4 font-body">
              Also known in the community as <strong className="text-gold-700">Corporate Awards &amp; Graphics</strong>
            </p>
          </div>
        </div>
      </section>

      {/* Section 1: About Us (Main Overview) */}
      <section className="py-16 md:py-24 bg-ivory-50">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12 items-start">
              <div>
                <h2 className="text-2xl md:text-3xl font-heading font-semibold text-ivory-800 mb-6">
                  Who We Are
                </h2>
                <div className="space-y-4 text-ivory-600 font-body leading-relaxed">
                  <p>
                    We are a <strong className="text-ivory-800">family-owned company</strong>, established in <strong className="text-ivory-800">1991</strong>, and proudly based in <strong className="text-ivory-800">Santa Ana, California</strong>. Known in our community as <strong className="text-ivory-800">Corporate Awards &amp; Graphics</strong> (or Corporate Awards &amp; Engraving), we&apos;ve spent over three decades helping organizations and individuals succeed with exceptional custom products and professional print services.
                  </p>
                  <p>
                    Our services span <strong className="text-ivory-800">custom awards, trophies, plaques, medals, and engraving</strong>, as well as <strong className="text-ivory-800">small format printing</strong> including business cards, flyers, brochures, catalogs, marketing materials, and promotional collateral. We also specialize in <strong className="text-ivory-800">large-format graphics</strong> such as banners, signs, vehicle wraps, and trade show displays, plus <strong className="text-ivory-800">branded promotional merchandise</strong>—all produced in-house for superior quality control.
                  </p>
                </div>
              </div>
              <div>
                <h3 className="text-lg font-heading font-semibold text-ivory-800 mb-4">
                  Our Clients
                </h3>
                <p className="text-ivory-600 font-body leading-relaxed mb-6">
                  We&apos;re honored to serve a diverse range of clients, from local businesses, schools, and nonprofits to <strong className="text-ivory-800">Fortune-100 companies</strong>. Whether you need business cards for your startup, a complete catalog for your product line, custom awards for your team, or a full corporate recognition program, we deliver with the same commitment to excellence.
                </p>
                <div className="bg-ivory-100 rounded-xl p-6 border border-ivory-300/50">
                  <h4 className="text-sm font-semibold text-gold-700 uppercase tracking-wide mb-3 font-body">What Sets Us Apart</h4>
                  <ul className="space-y-2">
                    {['In-house production for quality control', 'Reliable delivery and turnaround', 'Long-term client relationships', 'Decades of experience'].map((item, index) => (
                      <li key={index} className="flex items-start gap-2 text-ivory-600 font-body text-sm">
                        <CheckCircle className="w-4 h-4 text-gold-600 mt-0.5 flex-shrink-0" />
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 1.5: Our Services */}
      <section className="py-16 md:py-24 gradient-stone">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-ivory-800 mb-4">
              Our Services
            </h2>
            <p className="text-ivory-500 font-body max-w-2xl mx-auto">
              Comprehensive printing, engraving, and promotional solutions—all under one roof.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 max-w-6xl mx-auto">
            {services.map((service, index) => (
              <div 
                key={index}
                className="p-6 rounded-xl bg-ivory-50 border border-ivory-300/50 card-luxury"
                data-testid={`service-${index}`}
              >
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-gold-600 to-gold-500 flex items-center justify-center mb-4 shadow-luxury">
                  <service.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-ivory-800 mb-3">
                  {service.title}
                </h3>
                <ul className="space-y-1.5">
                  {service.items.map((item, idx) => (
                    <li key={idx} className="text-ivory-500 font-body text-sm flex items-center gap-2">
                      <Sparkles className="w-3 h-3 text-gold-500 flex-shrink-0" />
                      {item}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 2: Our Story */}
      <section className="py-16 md:py-24 gradient-pearl">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto text-center">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-ivory-800 mb-8">
              Our Story
            </h2>
            <div className="text-ivory-600 font-body leading-relaxed space-y-4 text-left md:text-center">
              <p>
                Founded in 1991, our company—known locally as <strong className="text-ivory-800">Corporate Awards &amp; Engraving</strong>—began as a small family operation with a simple mission: to provide high-quality recognition products with exceptional personal service. What started as a modest engraving shop has grown into a full-service printing, awards, and promotional products company serving clients across the nation.
              </p>
              <p>
                Over more than <strong className="text-ivory-800">30 years</strong>, we&apos;ve remained true to our family-owned roots while expanding our capabilities. Our commitment to <strong className="text-ivory-800">craftsmanship, reliability, and customer relationships</strong> has earned us the trust of thousands of satisfied clients—from local Little League teams to Fortune-100 corporations.
              </p>
              <p>
                Today, we continue to invest in the latest technology while preserving the hands-on attention to detail that has defined our work for over three decades.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Why Choose Us */}
      <section className="py-16 md:py-24 bg-ivory-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-2xl md:text-3xl font-heading font-semibold text-ivory-800 mb-4">
              Why Choose Us
            </h2>
            <p className="text-ivory-500 font-body max-w-2xl mx-auto">
              Experience the difference that three decades of dedication brings to every project.
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6 max-w-5xl mx-auto">
            {whyChooseUs.map((item, index) => (
              <div 
                key={index}
                className="p-6 rounded-xl bg-ivory-100 border border-ivory-300/50 card-luxury"
                data-testid={`why-choose-${index}`}
              >
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-gold-600 to-gold-500 flex items-center justify-center mb-4 shadow-luxury">
                  <item.icon className="w-6 h-6 text-white" />
                </div>
                <h3 className="text-lg font-heading font-semibold text-ivory-800 mb-2">
                  {item.title}
                </h3>
                <p className="text-ivory-500 font-body text-sm">
                  {item.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Section 4: Mission & Values */}
      <section className="py-16 md:py-24 gradient-stone">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="grid md:grid-cols-2 gap-12">
              {/* Mission */}
              <div className="bg-ivory-50 rounded-xl p-8 border border-ivory-300/50 shadow-luxury">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-gold-600 to-gold-500 flex items-center justify-center mb-6">
                  <Award className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-heading font-semibold text-ivory-800 mb-4">
                  Our Mission
                </h2>
                <p className="text-ivory-600 font-body leading-relaxed">
                  To help organizations recognize achievement with thoughtfully crafted products, dependable service, and lasting quality.
                </p>
              </div>
              
              {/* Values */}
              <div className="bg-ivory-50 rounded-xl p-8 border border-ivory-300/50 shadow-luxury">
                <div className="w-12 h-12 rounded-lg bg-gradient-to-br from-gold-600 to-gold-500 flex items-center justify-center mb-6">
                  <Heart className="w-6 h-6 text-white" />
                </div>
                <h2 className="text-2xl font-heading font-semibold text-ivory-800 mb-4">
                  Our Values
                </h2>
                <ul className="space-y-3">
                  {values.map((value, index) => (
                    <li key={index} className="flex items-center gap-3 text-ivory-600 font-body">
                      <div className="w-2 h-2 rounded-full bg-gold-500" />
                      <span className="font-medium">{value}</span>
                    </li>
                  ))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 5: Founder's Note */}
      <section className="py-16 md:py-24 bg-ivory-50">
        <div className="container mx-auto px-4">
          <div className="max-w-3xl mx-auto">
            <div className="relative bg-ivory-100 rounded-xl p-8 md:p-12 border border-ivory-300/50 shadow-luxury">
              <Quote className="absolute top-6 left-6 w-10 h-10 text-gold-500/20" />
              
              <div className="relative">
                <h2 className="text-2xl font-heading font-semibold text-ivory-800 mb-6 text-center">
                  A Note from Our Family
                </h2>
                <div className="text-ivory-600 font-body leading-relaxed space-y-4">
                  <p>
                    Thank you for considering us for your recognition and promotional needs. Since opening our doors in 1991, we&apos;ve had the privilege of helping thousands of organizations celebrate their people, their achievements, and their milestones.
                  </p>
                  <p>
                    As a family-owned business, we take personal pride in every product that leaves our facility. When you work with us, you&apos;re not just a number—you&apos;re a valued partner. We hold ourselves accountable to the same standards we&apos;d expect if we were the customer.
                  </p>
                  <p>
                    We&apos;re grateful for the trust our clients have placed in us over the years, and we look forward to earning yours.
                  </p>
                </div>
                <div className="mt-8 text-center">
                  <p className="text-gold-700 font-heading font-semibold">— The Engraveca Family</p>
                  <p className="text-ivory-500 font-body text-sm mt-1">Santa Ana, California</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="relative py-20 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-r from-gold-700 via-gold-600 to-gold-500" />
        <div className="absolute top-0 right-0 w-[500px] h-[500px] bg-white/10 rounded-full blur-[100px]" />
        
        <div className="relative container mx-auto px-4 text-center">
          <h2 className="text-3xl md:text-4xl font-heading font-semibold text-white mb-4">
            Ready to Work Together?
          </h2>
          <p className="text-gold-100 mb-8 max-w-2xl mx-auto font-body">
            Let us help you recognize achievement with quality products and reliable service.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
            <a 
              href="/contact"
              className="inline-flex items-center px-8 py-4 bg-white text-gold-700 hover:bg-ivory-100 rounded-lg shadow-luxury-lg transition-all duration-300 hover:scale-105 font-body font-semibold"
              data-testid="about-contact-cta"
            >
              Contact Us
            </a>
            <a 
              href="/products"
              className="inline-flex items-center px-8 py-4 border-2 border-white/30 bg-transparent text-white hover:bg-white/10 rounded-lg transition-all duration-300 font-body font-medium"
              data-testid="about-products-cta"
            >
              Browse Products
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default AboutPage;
